// MyProfileModal — «Мой профиль».
//
// Сотрудник правит сам: ФИО (разрешено 2026-07-20, docs/service/32 Ф5),
// контакты, фото и СВОЙ ПАРОЛЬ (секция ниже — раньше добровольной смены не
// было вовсе, только принудительная в ChangePasswordGate).
// Должность и локации — только чтение: их меняют в «Сотрудниках».
// ВНИМАНИЕ: смена почты меняет и логин входа (сервер синкает users.email).
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { DropdownSelect } from '@/components/ui/dropdown-select';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { errorText } from '@/lib/api';

import { changeMyPassword, uploadMyPhoto } from '../api/profile.api';
import { useMyProfile, useUpdateMyProfile } from '../hooks/useMyProfile';

import { InstallAppBlock } from './InstallAppBlock';
import { PushToggle } from './PushToggle';
import { TelegramBlock } from './TelegramBlock';

const ROLE_LABEL: Record<string, string> = {
  boss: 'Босс',
  manager: 'Управляющий',
  senior_admin: 'Старший администратор',
  admin: 'Администратор',
  teacher: 'Педагог',
};

/** Минимальная длина нового пароля — как в ChangePasswordGate. */
const MIN_PASSWORD_LENGTH = 12;

export function MyProfileModal({ onClose }: { onClose: () => void }): JSX.Element {
  const profile = useMyProfile();
  const update = useUpdateMyProfile();
  const p = profile.data;

  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [middleName, setMiddleName] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [birthDate, setBirthDate] = useState<string | null>(null);
  const [gender, setGender] = useState<'m' | 'f' | '' | null>(null);
  const [photoBust, setPhotoBust] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Секция «Сменить пароль» — свёрнута по умолчанию: большинство заходит
  // в профиль за фото/контактами, а не за паролем.
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNext, setPwNext] = useState('');
  const [pwRepeat, setPwRepeat] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwDone, setPwDone] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  async function handleChangePassword(): Promise<void> {
    setPwError(null);
    setPwDone(false);
    if (pwNext.length < MIN_PASSWORD_LENGTH) {
      setPwError(`Новый пароль слишком короткий: нужно минимум ${MIN_PASSWORD_LENGTH} символов.`);
      return;
    }
    if (pwNext !== pwRepeat) {
      setPwError('Новый пароль и повтор не совпадают.');
      return;
    }
    setPwSaving(true);
    try {
      await changeMyPassword(pwCurrent, pwNext);
      setPwCurrent('');
      setPwNext('');
      setPwRepeat('');
      setPwDone(true);
      setPwOpen(false);
    } catch (err) {
      setPwError(errorText(err));
    } finally {
      setPwSaving(false);
    }
  }

  // Значения инпутов: локальный стейт (если тронут) иначе из профиля.
  const firstVal = firstName ?? p?.first_name ?? '';
  const lastVal = lastName ?? p?.last_name ?? '';
  const middleVal = middleName ?? p?.middle_name ?? '';
  const phoneVal = phone ?? p?.phone ?? '';
  const emailVal = email ?? p?.email ?? '';
  const birthVal = birthDate ?? p?.birth_date ?? '';
  const genderVal = gender ?? p?.gender ?? '';

  async function handleSave(): Promise<void> {
    setError(null);
    try {
      await update.mutateAsync({
        first_name: firstVal.trim(),
        last_name: lastVal.trim(),
        middle_name: middleVal.trim() === '' ? null : middleVal.trim(),
        phone: phoneVal.trim() === '' ? null : phoneVal.trim(),
        email: emailVal.trim() === '' ? null : emailVal.trim(),
        birth_date: birthVal === '' ? null : birthVal,
        gender: genderVal === '' ? null : (genderVal as 'm' | 'f'),
      });
      onClose();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function handlePhoto(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      await uploadMyPhoto(file);
      await profile.refetch();
      setPhotoBust((n) => n + 1); // сбросить кэш <img>
    } catch (err) {
      setError(errorText(err));
    }
  }

  const initials = p ? `${p.last_name[0] ?? ''}${p.first_name[0] ?? ''}` : '';

  return (
    <Modal open onClose={onClose} size="sm" title="Мой профиль">
      <div className="space-y-3 px-6 py-5">
        {profile.isLoading ? (
          <p className="text-sm text-muted-foreground">Загрузка…</p>
        ) : !p ? (
          <p className="text-sm text-destructive">Не удалось загрузить профиль.</p>
        ) : (
          <>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-lavender-mist text-lg font-semibold text-ink-black">
                {p.photo_url ? (
                  <img
                    src={`${p.photo_url}?v=${photoBust}`}
                    alt="Фото"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              <label className="cursor-pointer text-sm text-primary hover:underline">
                Загрузить фото
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              </label>
            </div>

            <div className="grid grid-cols-2 max-lg:grid-cols-1 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="profile-last-name">
                  Фамилия
                </label>
                <Input
                  id="profile-last-name"
                  value={lastVal}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="profile-first-name">
                  Имя
                </label>
                <Input
                  id="profile-first-name"
                  value={firstVal}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="profile-middle-name">
                Отчество
              </label>
              <Input
                id="profile-middle-name"
                value={middleVal}
                onChange={(e) => setMiddleName(e.target.value)}
              />
            </div>
            <ReadRow label="Должность">{ROLE_LABEL[p.role] ?? p.role}</ReadRow>
            <ReadRow label="Локация">
              {p.branch_names.length > 0 ? p.branch_names.join(', ') : '—'}
            </ReadRow>

            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="profile-phone">
                Телефон
              </label>
              <Input
                id="profile-phone"
                value={phoneVal}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="profile-email">
                Почта
              </label>
              <Input
                id="profile-email"
                type="email"
                value={emailVal}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Эта почта используется и для входа в систему.
              </p>
            </div>
            <div className="grid grid-cols-2 max-lg:grid-cols-1 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Дата рождения</label>
                <Input
                  type="date"
                  value={birthVal}
                  onChange={(e) => setBirthDate(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Пол</label>
                <DropdownSelect
                  ariaLabel="Пол"
                  value={genderVal}
                  onChange={(next) => setGender(next as 'm' | 'f' | '')}
                  options={[
                    { value: '', label: '—' },
                    { value: 'm', label: 'Мужской' },
                    { value: 'f', label: 'Женский' },
                  ]}
                />
              </div>
            </div>

            {/* Смена собственного пароля (docs/service/32 Ф5). */}
            <div className="rounded-base border border-divider-gray">
              <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium"
                onClick={() => setPwOpen((v) => !v)}
                aria-expanded={pwOpen}
              >
                Сменить пароль
                <span className="text-muted-foreground">{pwOpen ? '−' : '+'}</span>
              </button>
              {pwOpen ? (
                <div className="space-y-2 border-t border-divider-gray px-3 py-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium" htmlFor="pw-current">
                      Текущий пароль
                    </label>
                    <Input
                      id="pw-current"
                      type="password"
                      autoComplete="current-password"
                      value={pwCurrent}
                      onChange={(e) => setPwCurrent(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium" htmlFor="pw-next">
                      Новый пароль
                    </label>
                    <Input
                      id="pw-next"
                      type="password"
                      autoComplete="new-password"
                      value={pwNext}
                      onChange={(e) => setPwNext(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Минимум {MIN_PASSWORD_LENGTH} символов.
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium" htmlFor="pw-repeat">
                      Повторите новый
                    </label>
                    <Input
                      id="pw-repeat"
                      type="password"
                      autoComplete="new-password"
                      value={pwRepeat}
                      onChange={(e) => setPwRepeat(e.target.value)}
                    />
                  </div>
                  {pwError ? (
                    <p className="rounded-base bg-destructive/10 px-3 py-2 text-sm text-destructive">
                      {pwError}
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    onClick={handleChangePassword}
                    disabled={pwSaving || pwCurrent === '' || pwNext === ''}
                  >
                    {pwSaving ? 'Меняем…' : 'Сменить пароль'}
                  </Button>
                </div>
              ) : null}
            </div>

            {pwDone ? (
              <p className="rounded-base bg-spring-green/20 px-3 py-2 text-sm text-ink-black">
                Пароль изменён.
              </p>
            ) : null}

            {error ? (
              <p className="rounded-base bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            {/* План 110: установка на экран телефона и пуши. Оба блока сами
                решают, показываться ли: на десктопе и в уже установленном
                приложении их нет. */}
            <InstallAppBlock />
            <PushToggle />
            {/* План 155 Ф3: копии уведомлений в Telegram и выбор видов. */}
            <TelegramBlock />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>
                Закрыть
              </Button>
              <Button type="button" onClick={handleSave} disabled={update.isPending}>
                {update.isPending ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function ReadRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-divider-gray py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium">{children}</span>
    </div>
  );
}
